import { useState } from "react";
import { GOALS, DAYS, DURATIONS, EQUIPMENT } from "../data/onboarding";
import { MOOD_LABELS } from "../utils/mood";
import { BrandGlyph } from "./AuthScreen";
import "../styles/welcome-surface.css";

/* Setting someone up for a mental-health product, in this order:
     1  what they'd like to feel more of      (emotional — this comes first)
     2  how much movement feels realistic
     3  what's actually around them
     4  a few body details, entirely optional and skippable
     5  the check-in itself, so the loop is established before the app opens
   Nothing before the final screen is a toll gate, and the person is never
   asked for their weight in order to get through a door. */
const TOTAL_STEPS = 7;
const QUESTION_STEPS = [1, 2, 3, 4, 5];

/* The action rail. `hint` names what is still missing rather than leaving
   someone in front of a button that won't press and won't say why.

   One button, always. A second full-width button calling the same handler is a
   false choice dressed as a kindness — where a step is genuinely skippable the
   single button says so in its label instead. */
function StepFoot({ hint = "", label = "Continue", disabled = false, onNext }) {
  return (
    <div className="ws-ob-foot">
      <p className="ws-need" role="status" aria-live="polite">
        {hint}
      </p>
      <button
        type="button"
        className="btn btn-primary btn-lg btn-full"
        disabled={disabled}
        onClick={onNext}
      >
        {label}
      </button>
    </div>
  );
}

/* The selection mark. Fixed 20px whether or not it is filled, so choosing
   something never changes a row's size or pushes anything around. */
function OptionCheck() {
  return (
    <span className="ws-option-check" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

export default function OnboardingFlow({ username, onComplete }) {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [animKey, setAnimKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  /* Deliberately kept out of `data`: this is the person trying the check-in,
     not a profile field. It shapes the copy on the way through and nothing
     else — the real check-in is waiting for them on the home screen. */
  const [tryMood, setTryMood] = useState(null);

  const [data, setData] = useState({
    primaryGoal: "",
    experienceLevel: "intermediate",
    trainingDaysPerWeek: 4,
    sessionDuration: "45-60 min",
    equipment: [],
    limitations: [],
    additionalNotes: "",
    heightFeet: "",
    heightInches: "",
    weight: "",
    targetWeight: "",
    age: "",
    sex: "",
  });

  const set = (field, val) => setData((d) => ({ ...d, [field]: val }));

  const toggleArr = (field, val) => {
    setData((d) => {
      const arr = d[field];
      if (field === "equipment") {
        const allIds = EQUIPMENT.map((e) => e.id);
        if (val === "full_gym") {
          return { ...d, [field]: arr.includes("full_gym") ? [] : [...allIds] };
        }
        if (val === "bodyweight") {
          return { ...d, [field]: arr.includes("bodyweight") ? [] : ["bodyweight"] };
        }
        const without = arr.filter((v) => v !== "bodyweight");
        const toggled = without.includes(val)
          ? without.filter((v) => v !== val)
          : [...without, val];
        const individualIds = allIds.filter((id) => id !== "full_gym" && id !== "bodyweight");
        const hasAll = individualIds.every((id) => toggled.includes(id));
        if (hasAll && !toggled.includes("full_gym")) toggled.push("full_gym");
        else if (!hasAll) {
          const idx = toggled.indexOf("full_gym");
          if (idx !== -1) toggled.splice(idx, 1);
        }
        return { ...d, [field]: toggled };
      }
      return {
        ...d,
        [field]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val],
      };
    });
  };

  const goNext = () => {
    setDir(1);
    setAnimKey((k) => k + 1);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const goBack = () => {
    setDir(-1);
    setAnimKey((k) => k + 1);
    setStep((s) => Math.max(s - 1, 0));
  };

  /* Only the two questions the app genuinely cannot work without are gated.
     Body details and the check-in are both optional — the macro estimate
     falls back to population averages, which is a fine trade for never
     making someone type their weight to get in. */
  const canAdvance = () => {
    switch (step) {
      case 1: return !!data.primaryGoal;
      case 2: return !!data.trainingDaysPerWeek && !!data.sessionDuration;
      case 3: return data.equipment.length > 0;
      default: return true;
    }
  };

  const hintFor = () => {
    if (canAdvance()) return "";
    if (step === 1) return "Pick whichever is closest — nothing here is binding.";
    if (step === 3) return "Pick at least one. “Just me and a floor” is a real answer.";
    return "";
  };

  /* Step 4 is entirely optional, so its one action says which of the two things
     it is actually doing rather than offering both as separate buttons. */
  const hasAnyDetail = Boolean(
    data.heightFeet || data.heightInches || data.weight || data.age || data.sex || data.targetWeight
  );

  const goalSummary = GOALS.find((g) => g.id === data.primaryGoal)?.label || "";
  const showProgress = step > 0 && step < TOTAL_STEPS - 1;
  const greeting = username ? `Hi, ${username}.` : "Hi there.";

  return (
    <main className="ws-screen" aria-label="Getting set up">
      <div className="ws-wash" aria-hidden="true" />

      {showProgress && (
        <div className="ws-ob-head">
          <button type="button" className="ws-back" onClick={goBack} aria-label="Go back a step">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div
            className="ws-steps"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={QUESTION_STEPS.length}
            aria-valuenow={step}
            aria-valuetext={`Question ${step} of ${QUESTION_STEPS.length}`}
          >
            {QUESTION_STEPS.map((n) => (
              <span
                key={n}
                className="ws-step"
                data-state={n < step ? "done" : n === step ? "current" : "todo"}
              />
            ))}
          </div>
        </div>
      )}

      {/* The final step keeps the back affordance but has no progress to show.
          It lives out here beside the progress header rather than inside the
          animated wrapper below — nested in a centred flex child it rendered
          17px lower, so the one piece of persistent chrome in the flow jumped
          on its last transition. */}
      {step === TOTAL_STEPS - 1 && (
        <div className="ws-ob-head">
          <button
            type="button"
            className="ws-back"
            onClick={goBack}
            disabled={saving}
            aria-label="Go back a step"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </div>
      )}

      <div
        className="ws-enter"
        key={animKey}
        data-dir={dir}
        style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}
      >
        {/* ── 0 · A greeting, not a form ───────────────────────────────── */}
        {step === 0 && (
          <>
            <div className="ws-stage">
              <div className="ws-mark">
                <span className="ws-mark-aura ds-breathe" aria-hidden="true" />
                <span className="ws-mark-glyph">
                  <BrandGlyph size={44} />
                </span>
              </div>

              <p className="ws-wordmark">{greeting}</p>
              <h1 className="ws-hero">Let’s shape this around you.</h1>
              <p className="ws-hero-sub">
                A few short questions about how you’ve been and how
                you’d like to move. No wrong answers, and you can change
                any of it later.
              </p>
            </div>
            <div className="ws-actions">
              <button type="button" className="btn btn-primary btn-lg btn-full" onClick={goNext}>
                I’m ready
              </button>
            </div>
          </>
        )}

        {/* ── 1 · How they'd like to feel ──────────────────────────────── */}
        {step === 1 && (
          <>
            <div className="ws-ob-body">
              <div className="ws-ob-ask">
                <h1 className="ws-q">What would you like to feel more of?</h1>
                <p className="ws-hint">
                  Whatever’s closest to how things have been lately. This is
                  the part that shapes everything else.
                </p>
              </div>

              <div className="ws-ob-answers">
                {/* Same ids, same data, same order — but the two body-composition
                    answers are set behind a divider and their own line, so the
                    question stops implying that easing weight down is an answer
                    to how you would like to feel. */}
                <div className="ws-options ws-group" role="group" aria-label="What you’d like to feel more of">
                  {GOALS.slice(0, 3).map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      className="ws-option"
                      aria-pressed={data.primaryGoal === g.id}
                      onClick={() => set("primaryGoal", g.id)}
                    >
                      <OptionCheck />
                      <span>
                        <span className="ws-option-label">{g.label}</span>
                        <span className="ws-option-desc">{g.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>

                <p className="ws-subhead" id="ob-body-goal-label">
                  Or, if you have a body goal in mind
                </p>

                <div className="ws-options" role="group" aria-labelledby="ob-body-goal-label">
                  {GOALS.slice(3).map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      className="ws-option"
                      aria-pressed={data.primaryGoal === g.id}
                      onClick={() => set("primaryGoal", g.id)}
                    >
                      <OptionCheck />
                      <span>
                        <span className="ws-option-label">{g.label}</span>
                        <span className="ws-option-desc">{g.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <StepFoot hint={hintFor()} disabled={!canAdvance()} onNext={goNext} />
          </>
        )}

        {/* ── 2 · How much movement feels realistic ────────────────────── */}
        {step === 2 && (
          <>
            <div className="ws-ob-body">
              <div className="ws-ob-ask">
                <h1 className="ws-q">How often would you like to move?</h1>
                <p className="ws-hint">
                  A rough guess is plenty — this shifts with how you’re doing.
                </p>
              </div>

              <div className="ws-ob-answers">
                <div className="ws-group">
                  {/* The unit belongs above the control. Six bare digits with the
                      explanation underneath meant reading the answer first and
                      the question second. */}
                  <span className="ws-label-text" id="ob-days-label">Days a week</span>
                  <div className="ws-choices ws-choices--days" role="group" aria-labelledby="ob-days-label">
                    {DAYS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        className="ws-choice"
                        aria-pressed={data.trainingDaysPerWeek === d}
                        aria-label={`${d} days a week`}
                        onClick={() => set("trainingDaysPerWeek", d)}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <h2 className="ws-q ws-q--sub">And roughly how long?</h2>
                <p className="ws-hint">Short sessions count just as much.</p>

                <div className="ws-choices ws-choices--pair ws-group" role="group" aria-label="Session length">
                  {DURATIONS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      className="ws-choice"
                      aria-pressed={data.sessionDuration === d}
                      onClick={() => set("sessionDuration", d)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <StepFoot hint={hintFor()} disabled={!canAdvance()} onNext={goNext} />
          </>
        )}

        {/* ── 3 · What's actually around them ──────────────────────────── */}
        {step === 3 && (
          <>
            <div className="ws-ob-body">
              <div className="ws-ob-ask">
                <h1 className="ws-q">What do you have around you?</h1>
                <p className="ws-hint">
                  Pick as many as fit. Most of what this asks of you needs
                  nothing at all.
                </p>
              </div>

              <div className="ws-ob-answers">
                {/* Rows, not pills. Half these labels were too wide to pair, so
                    the wrap left four chips sitting alone in half-empty rows. */}
                <div className="ws-options ws-group" role="group" aria-label="What you have access to">
                  {EQUIPMENT.map((eq) => (
                    <button
                      key={eq.id}
                      type="button"
                      className="ws-option ws-option--compact"
                      aria-pressed={data.equipment.includes(eq.id)}
                      onClick={() => toggleArr("equipment", eq.id)}
                    >
                      <OptionCheck />
                      <span className="ws-option-label">{eq.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <StepFoot hint={hintFor()} disabled={!canAdvance()} onNext={goNext} />
          </>
        )}

        {/* ── 4 · Optional details ─────────────────────────────────────
            Nothing here is required. It used to be impossible to enter the
            app without typing your body weight, which is the sharpest version
            of a risk this product should be taking least of all. */}
        {step === 4 && (
          <>
            <div className="ws-ob-body">
              <div className="ws-ob-ask">
                <h1 className="ws-q">A few details, if you’d like.</h1>
                <p className="ws-hint">
                  All optional. They only make the food guidance in the
                  background a little less generic — none of it is a target, and
                  you can add or change any of it in settings later.
                </p>
              </div>

              {/* One placeholder idiom for the whole grid. The height fields
                  read "5" and "10", which in a number input is indistinguishable
                  from a typed value — the field looked pre-filled at 5'10" and
                  people scrolled straight past it. Every empty field here now
                  says the same word, and the sentence that used to be a
                  placeholder is a hint where a hint belongs. */}
              <div className="ws-ob-answers">
                <div className="ws-fields ws-group">
                  <div className="ws-field ws-field--wide">
                    <span className="ws-label-text" id="ob-height-label">Height</span>
                    <div className="ws-pair" role="group" aria-labelledby="ob-height-label">
                      <div className="ws-input-wrap">
                        <input
                          type="number"
                          inputMode="numeric"
                          placeholder="optional"
                          value={data.heightFeet}
                          onChange={(e) => set("heightFeet", e.target.value)}
                          aria-label="Height, feet"
                        />
                        <span className="ws-unit" aria-hidden="true">ft</span>
                      </div>
                      <div className="ws-input-wrap">
                        <input
                          type="number"
                          inputMode="numeric"
                          placeholder="optional"
                          min="0"
                          max="11"
                          value={data.heightInches}
                          onChange={(e) => set("heightInches", e.target.value)}
                          aria-label="Height, inches"
                        />
                        <span className="ws-unit" aria-hidden="true">in</span>
                      </div>
                    </div>
                  </div>

                  <div className="ws-field">
                    <label htmlFor="ob-weight">Weight</label>
                    <div className="ws-input-wrap">
                      <input
                        id="ob-weight"
                        type="number"
                        inputMode="decimal"
                        placeholder="optional"
                        value={data.weight}
                        onChange={(e) => set("weight", e.target.value)}
                      />
                      <span className="ws-unit" aria-hidden="true">lbs</span>
                    </div>
                  </div>

                  <div className="ws-field">
                    <label htmlFor="ob-age">Age</label>
                    <div className="ws-input-wrap">
                      <input
                        id="ob-age"
                        type="number"
                        inputMode="numeric"
                        placeholder="optional"
                        value={data.age}
                        onChange={(e) => set("age", e.target.value)}
                      />
                      <span className="ws-unit" aria-hidden="true">yrs</span>
                    </div>
                  </div>

                  <div className="ws-field ws-field--wide">
                    <span className="ws-label-text" id="ob-sex-label">Sex</span>
                    <div className="ws-choices ws-choices--pair" role="group" aria-labelledby="ob-sex-label">
                      {["Male", "Female"].map((s) => (
                        <button
                          key={s}
                          type="button"
                          className="ws-choice"
                          aria-pressed={data.sex === s}
                          onClick={() => set("sex", s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <p className="ws-field-hint">Used for the metabolic estimate only.</p>
                  </div>

                  <div className="ws-field ws-field--wide">
                    <label htmlFor="ob-target">A weight you’re heading toward</label>
                    <div className="ws-input-wrap">
                      <input
                        id="ob-target"
                        type="number"
                        inputMode="decimal"
                        placeholder="optional"
                        value={data.targetWeight}
                        onChange={(e) => set("targetWeight", e.target.value)}
                        aria-describedby="ob-target-hint"
                      />
                      <span className="ws-unit" aria-hidden="true">lbs</span>
                    </div>
                    <p className="ws-field-hint" id="ob-target-hint">
                      Leave this blank if you’d rather not.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* One button. It said "Continue" and "Skip for now" one above the
                other, both calling the same thing with nothing gated — a false
                choice on the screen designed to ask the least of anyone. The
                label now just tells the truth about what pressing it does. */}
            <StepFoot label={hasAnyDetail ? "Continue" : "Skip for now"} onNext={goNext} />
          </>
        )}

        {/* ── 5 · The check-in, tried once ─────────────────────────────
            The loop is mood → movement → mood, so the person meets the mood
            scale before the app opens rather than after. */}
        {step === 5 && (
          <>
            <div className="ws-ob-body">
              <div className="ws-ob-ask">
                <h1 className="ws-q">And this is the check-in.</h1>
                <p className="ws-hint">
                  Each morning the app asks one question: how are you? Have a go
                  now — wherever today sits is the right answer.
                </p>
              </div>

              <div className="ws-ob-answers">
                <div className="ds-scale ws-group" role="group" aria-label="How you’re doing right now">
                  {MOOD_LABELS.map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      className="ds-scale-option"
                      data-mood={i + 1}
                      aria-pressed={tryMood === i + 1}
                      aria-label={`Mood: ${label}`}
                      onClick={() => setTryMood(i + 1)}
                    >
                      <span className="ds-mood-dot" data-mood={i + 1} aria-hidden="true" />
                      {label}
                    </button>
                  ))}
                </div>

                <p className="ws-hint ws-group" role="status" aria-live="polite">
                  {tryMood
                    ? "That’s the whole thing. It asks again after you move, and the difference between those two answers is what the app learns from."
                    : "Nothing is recorded here — the real one is waiting for you inside, tomorrow morning and every morning."}
                </p>
              </div>
            </div>

            <StepFoot onNext={goNext} />
          </>
        )}

        {/* ── 6 · The close ───────────────────────────────────────────── */}
        {step === 6 && (
          <>
            <div className="ws-stage">
              <span className="ws-seal" aria-hidden="true">
                <svg width="84" height="84" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="1" opacity="0.2" />
                  <circle
                    cx="32" cy="32" r="30"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    className="ws-seal-circle"
                  />
                  <polyline
                    points="21,33 28,41 44,24"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    className="ws-seal-tick"
                  />
                </svg>
              </span>

              <h1 className="ws-hero">That’s everything.</h1>
              <p className="ws-hero-sub">
                Here’s what we’ll start from. None of it is fixed.
              </p>

              {/* No body measurements here. They were entered two screens ago,
                  and the last thing someone sees before going in should be
                  what they're joining, not their weight read back to them. */}
              <dl className="ws-summary">
                <div className="ws-summary-row">
                  <dt className="ws-summary-key">Toward</dt>
                  <dd className="ws-summary-val">{goalSummary}</dd>
                </div>
                <div className="ws-summary-row">
                  <dt className="ws-summary-key">Movement</dt>
                  <dd className="ws-summary-val">
                    {data.trainingDaysPerWeek} days a week, {data.sessionDuration}
                  </dd>
                </div>
                <div className="ws-summary-row">
                  <dt className="ws-summary-key">Check-ins</dt>
                  <dd className="ws-summary-val">
                    Each morning, and after you move
                  </dd>
                </div>
              </dl>

              <p className="ws-footnote">
                The more you check in, the better this gets at noticing what
                actually helps you feel better.
              </p>
            </div>

            <div className="ws-actions">
              <div role="status" aria-live="polite">
                {saveError && (
                  <p className="ws-notice">
                    That didn’t save — {typeof saveError === "string" ? saveError : "give it another go in a moment."}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-lg btn-full"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  setSaveError(null);
                  try {
                    await onComplete(data);
                  } catch (err) {
                    console.error("Onboarding save error:", err);
                    setSaveError(err?.message || "Unknown error");
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? (
                  <>
                    <span className="ws-spinner" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  "Take me in"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
